import { AppPageLayout } from "@/components/common/AppPageLayout";
import {
    formPageRouteShellClass,
    formPageShellFillClass,
    publicFormRoutePaddingClass,
} from "@/features/pages/components/formPageLayout";
import { useSelectFormById } from "@/features/forms/api/selectFormById";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import FormPage from "@/features/pages/components/FormPage";
import { useMsAdlFormPageContext } from "@/features/pages/hooks/useMsAdlFormPageContext";
import useFormPage from "@/features/pages/hooks/useFormPage";
import { readPublicFormSide } from "@/features/pages/utils/publicFormSide";
import { formatTrackerLateralityBanner } from "@/features/pages/utils/trackerLateralityBanner";
import { useEntryValuesStore } from "@/features/inputs/hooks/useEntryValuesStore";
import { formNameLabel } from "@/features/navigation/services/entityDisplayLabel";
import { useProfileByIdQuerySafe } from "@/features/profile/api/selectProfile";
import ChooseValidatorOrganisationDialog from "@/features/validations/components/ChooseValidatorOrganisationDialog";
import { validatorOrganisations } from "@/features/validations/utils/validatorOrganisations";
import { useValidationByIdQuery } from "@/features/validations/api/selectValidationById";
import { ValidationComments } from "@/features/validations/comments/components/ValidationComments";
import { COMMENT_SURFACE_ATTRIBUTE } from "@/features/validations/comments/utils/pageSelection";
import {
    submitValidatorFormReview,
    validatorReviewAnswers,
} from "@/features/validations/utils/submitValidatorFormReview";
import { readValidationFormSession } from "@/features/validations/utils/validationFormSession";
import { useAuth } from "@/utils/auth";
import { useNav } from "@/utils/nav";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "@patient-watch/ui/sonner";
import { useEffect, useState } from "react";
import { trackEvent } from "@/utils/trackEvent";
import Spinner from "@patient-watch/ui/spinner";

/** The edge function returns `{ error }` in a 400 body; surface it instead of a generic failure. */
async function validatorSubmitErrorMessage(error: unknown): Promise<string> {
    const context = (error as { context?: unknown } | null)?.context;
    if (context instanceof Response) {
        try {
            const body = (await context.clone().json()) as { error?: unknown };
            if (typeof body.error === "string") return body.error;
        } catch {
            /* fall through to the error's own message */
        }
    }
    return error instanceof Error ? error.message : "";
}

const Page = () => {
    const { validationId, formId, pageIndex } = Route.useParams();
    const navigate = useNavigate();
    const auth = useAuth();
    const { data: signedInProfile } = useProfileByIdQuerySafe(auth.isAuthenticated ? auth.id : null);
    const validatorOrgs = validatorOrganisations(signedInProfile);
    const [isChoosingOrganisation, setIsChoosingOrganisation] = useState(false);
    const { data: validation } = useValidationByIdQuery(validationId);
    const getEntryValues = useEntryValuesStore((state) => state.getEntryValues);
    const [isFinishingReview, setIsFinishingReview] = useState(false);
    const [session] = useState(() => readValidationFormSession(validationId, formId));
    const { locale } = useAppLocale();
    const { data: form } = useSelectFormById(formId, locale);
    const numericPageIndex = Number(pageIndex);
    const lateralityBanner = form?.is_side_specific
        ? formatTrackerLateralityBanner(readPublicFormSide(session?.entryId), locale)
        : null;

    useNav([
        { label: "Validate", route: { to: "/validate" } },
        {
            label: formNameLabel(form, validation?.entity_name || "Review"),
            route: { to: "/validate/$validationId/forms/$formId", params: { validationId, formId } },
        },
    ]);

    useEffect(() => {
        if (session) return;
        void navigate({
            to: "/validate/$validationId/forms/$formId",
            params: { validationId, formId },
            replace: true,
        });
    }, [formId, navigate, session, validationId]);

    const submitReview = (validatorOrganisationId?: string) => {
        if (!session) return;
        setIsChoosingOrganisation(false);
        setIsFinishingReview(true);
        void submitValidatorFormReview({
            validationId,
            formId,
            entryId: session.entryId,
            asSessionUser: true,
            validatorOrganisationId,
            entryValues: validatorReviewAnswers(session.entryId, getEntryValues()),
        })
            .then(() => {
                void navigate({
                    to: "/validate/$validationId/forms/$formId/results",
                    params: { validationId, formId },
                });
            })
            .catch(async (error: unknown) => {
                setIsFinishingReview(false);
                const detail = await validatorSubmitErrorMessage(error);
                toast.error(
                    `Could not save these answers. Stay on this page and submit again.${detail ? ` (${detail})` : ""}`
                );
            });
    };

    const {
        currentPage,
        pageCount,
        msAdlSelectionInputId,
        renderPageIndex,
        isLastPageOverride,
        progressPageIndex,
        progressPageTotal,
    } = useMsAdlFormPageContext(form, numericPageIndex);

    const {
        form: reactHookForm,
        onSubmit,
        prevPage,
        nextPage,
        isLastPage,
        visitedPageCount,
    } = useFormPage({
        pageCount,
        inputs: currentPage?.inputs ?? [],
        nextEntry: null,
        form,
        formType: "tracker",
        isLastPageOverride,
        skipDbWrite: true,
        entryId: session?.entryId,
        diaryId: session?.diaryId,
        trackerId: session?.trackerId,
        nextPageFn: (newPageIndex) => {
            navigate({
                to: Route.to,
                params: { validationId, formId, pageIndex: String(newPageIndex) },
                resetScroll: false,
            });
        },
        prevPageFn: (newPageIndex) => {
            navigate({
                to: Route.to,
                params: { validationId, formId, pageIndex: String(newPageIndex) },
                resetScroll: false,
            });
        },
        successFn: () => {
            navigate({
                to: Route.to,
                params: { validationId, formId, pageIndex: String(numericPageIndex + 1) },
            });
        },
        completeFn: () => {
            if (!session) return;
            trackEvent("form_completed", { form_id: formId });
            // Almost always one organisation, used without asking; the server checks it either way.
            if (validatorOrgs.length > 1) {
                setIsChoosingOrganisation(true);
                return;
            }
            submitReview(validatorOrgs[0]?.organisation_id);
        },
    });

    if (!session) {
        return (
            <section className="flex flex-col justify-center items-center gap-3 py-24">
                <Spinner className="w-10 h-10" />
                <p className="text-slate-500 text-sm">Opening the form...</p>
            </section>
        );
    }

    if (isFinishingReview) {
        return (
            <section className="flex flex-col justify-center items-center gap-3 py-24">
                <Spinner className="w-10 h-10" />
                <p className="text-slate-500 text-sm">Saving your answers...</p>
            </section>
        );
    }

    if (!form || !currentPage) return null;

    return (
        <AppPageLayout width="md" padding="sm" className={`${publicFormRoutePaddingClass} ${formPageRouteShellClass}`}>
            <ChooseValidatorOrganisationDialog
                open={isChoosingOrganisation}
                organisations={validatorOrgs}
                onCancel={() => setIsChoosingOrganisation(false)}
                onConfirm={submitReview}
            />
            <ValidationComments entity={{ formId }} pageIndex={numericPageIndex} validationId={validationId} selectable>
                {/* Answer choices are select-none; let reviewers select their wording to comment on it. */}
                <div
                    className={`${formPageShellFillClass} [&_.select-none]:!select-text`}
                    {...{ [COMMENT_SURFACE_ATTRIBUTE]: "" }}
                >
                    <FormPage
                        pageIndex={numericPageIndex}
                        form={form}
                        route={Route}
                        formType="tracker"
                        reactHookForm={reactHookForm}
                        onSubmit={onSubmit}
                        prevPage={prevPage}
                        nextPage={nextPage}
                        isLastPage={isLastPageOverride ?? isLastPage}
                        visitedPageCount={visitedPageCount}
                        msAdlSelectionInputId={msAdlSelectionInputId}
                        renderPageIndex={renderPageIndex}
                        progressPageIndex={progressPageIndex}
                        progressPageTotal={progressPageTotal}
                        lateralityBanner={lateralityBanner}
                    />
                </div>
            </ValidationComments>
        </AppPageLayout>
    );
};

export const Route = createFileRoute("/_app/validate/$validationId/forms/$formId/page/$pageIndex")({
    component: Page,
});
